import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Calendar, Copy, Check, ExternalLink, Sparkles, Pencil, Save, X as XIcon, Send, Eye, MousePointerClick } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Spotlight Calendar — admin-only weekly social-post planner.
 *
 * Renders the next 12 ISO weeks of automatic Tool-of-the-Week picks with
 * pre-generated X and LinkedIn copy. Each row has copy-to-clipboard
 * buttons so the admin can fire off Monday's posts in seconds.
 *
 * The "Set custom pitch" inline editor writes to the partner's
 * `weekly_spotlight_pitch` field — overrides the auto-copy without
 * touching anything else. Leave blank to keep the auto-template.
 */
export default function AdminSpotlightCalendar() {
  const [weeks, setWeeks] = useState([]);
  const [poolSize, setPoolSize] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);
  const [editingSlug, setEditingSlug] = useState(null);
  const [pitchDraft, setPitchDraft] = useState('');
  const [savingPitch, setSavingPitch] = useState(false);
  // Past sends keyed by `${iso_year}-${iso_week}` so we can show open/click metrics
  const [sendsByWeek, setSendsByWeek] = useState({});
  const [broadcasting, setBroadcasting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [calRes, sendsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/affiliate-partners/spotlight-calendar?weeks=12`),
        axios.get(`${API_URL}/api/admin/spotlight/sends?limit=24`),
      ]);
      setWeeks(calRes.data.weeks || []);
      setPoolSize(calRes.data.pool_size || 0);
      const map = {};
      (sendsRes.data.sends || []).forEach((s) => {
        map[`${s.iso_year}-${s.iso_week}`] = s;
      });
      setSendsByWeek(map);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load spotlight calendar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success('Copied');
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1800);
    } catch {
      toast.error('Copy failed — select manually');
    }
  };

  const startEditing = (week) => {
    setEditingSlug(week.partner.slug);
    setPitchDraft(week.partner.weekly_spotlight_pitch || '');
  };

  const cancelEditing = () => {
    setEditingSlug(null);
    setPitchDraft('');
  };

  const savePitch = async (slug) => {
    setSavingPitch(true);
    try {
      await axios.patch(`${API_URL}/api/admin/affiliate-partners/${slug}`, {
        weekly_spotlight_pitch: pitchDraft.trim() || null,
      });
      toast.success(pitchDraft.trim() ? 'Custom pitch saved' : 'Reverted to auto-template');
      cancelEditing();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingPitch(false);
    }
  };

  const sendBroadcastNow = async () => {
    if (!window.confirm("Send this week's Tool of the Week newsletter to ALL subscribed users right now? (Idempotent — won't double-send if already sent.)")) {
      return;
    }
    setBroadcasting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/spotlight/send-weekly-broadcast`);
      const d = res.data || {};
      if (d.status === 'already_sent') {
        toast.info(`Already sent this week (${d.recipient_count} recipients)`);
      } else if (d.status === 'no_recipients') {
        toast.warning('No subscribed users to send to yet');
      } else if (d.status === 'no_partners') {
        toast.warning('No active affiliate partners');
      } else {
        toast.success(`Broadcast complete — sent ${d.sent_count}/${d.recipient_count}`);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Broadcast failed');
    } finally {
      setBroadcasting(false);
    }
  };

  const sendReminderNow = async () => {
    setBroadcasting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/spotlight/send-admin-reminder`);
      toast.success(`Admin reminder sent to ${res.data?.sent || 0} admin(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reminder send failed');
    } finally {
      setBroadcasting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-spotlight-calendar-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2">
              <Calendar className="w-6 h-6 text-[#00F0FF]" />
              Spotlight Calendar
            </h1>
            <p className="text-sm text-neutral-400 mt-1">
              Next 12 weeks of auto-rotated Tool of the Week picks · pool: {poolSize} active partners
              {' · '}auto-blasts Mondays 9:00 UTC · admin reminder Sundays 18:00 UTC
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={sendReminderNow}
              variant="outline"
              disabled={broadcasting}
              className="border-white/20 text-white"
              data-testid="send-admin-reminder-btn"
            >
              {broadcasting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Test admin reminder
            </Button>
            <Button
              onClick={sendBroadcastNow}
              disabled={broadcasting}
              className="bg-[#00F0FF] text-black hover:bg-[#00C2CC] font-semibold"
              data-testid="send-broadcast-now-btn"
            >
              {broadcasting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Send this week now
            </Button>
            <Button
              onClick={load}
              variant="outline"
              className="border-white/20 text-white"
              data-testid="refresh-calendar-btn"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Calendar */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
          </div>
        ) : weeks.length === 0 ? (
          <div className="text-center text-neutral-500 py-20" data-testid="calendar-empty">
            No active partners — add one in Affiliate Partners first.
          </div>
        ) : (
          <div className="space-y-4">
            {weeks.map((w) => {
              const isEditing = editingSlug === w.partner.slug;
              const rowKey = `${w.iso_year}-${w.iso_week}`;
              const sent = sendsByWeek[rowKey];
              return (
                <div
                  key={rowKey}
                  className={`rounded-2xl border p-4 md:p-5 ${
                    w.is_current
                      ? 'border-[#00F0FF]/60 bg-[#00F0FF]/5'
                      : 'border-white/10 bg-neutral-900/40'
                  }`}
                  data-testid={`calendar-row-${rowKey}`}
                >
                  {/* Top row: week + partner */}
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-4">
                    <div className="shrink-0 text-xs uppercase tracking-wider text-neutral-500 md:w-44">
                      <div className="font-mono">
                        ISO {w.iso_year}-W{String(w.iso_week).padStart(2, '0')}
                      </div>
                      <div className="text-neutral-400 mt-0.5">
                        {w.week_start} → {w.week_end}
                      </div>
                      {w.is_current && (
                        <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/40 text-[10px] font-bold text-[#00F0FF]">
                          <Sparkles className="w-3 h-3" /> LIVE NOW
                        </div>
                      )}
                      {sent && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`stats-${rowKey}`}>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/40 border border-white/10 text-[10px] text-neutral-300">
                            <Send className="w-2.5 h-2.5" /> {sent.sent_count}/{sent.recipient_count}
                          </span>
                          {sent.open_count > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/40 border border-white/10 text-[10px] text-neutral-300">
                              <Eye className="w-2.5 h-2.5" /> {sent.open_count}
                            </span>
                          )}
                          {sent.click_count > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#00FF94]/10 border border-[#00FF94]/40 text-[10px] text-[#00FF94]">
                              <MousePointerClick className="w-2.5 h-2.5" /> {sent.click_count}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-black/60 border border-white/5 flex items-center justify-center overflow-hidden p-1.5">
                      {w.partner.logo_url ? (
                        <img src={w.partner.logo_url} alt={w.partner.name} className="w-full h-full object-contain" loading="lazy" />
                      ) : (
                        <Sparkles className="w-5 h-5 text-[#00F0FF]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-white truncate">{w.partner.name}</h3>
                        <a
                          href={w.live_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#00F0FF] hover:text-[#00C2CC]"
                          data-testid={`storefront-link-${w.partner.slug}-${rowKey}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                      <p className="text-xs text-neutral-400 truncate">
                        {w.partner.weekly_spotlight_pitch
                          ? <span className="text-[#00FF94]">Custom pitch · {w.partner.weekly_spotlight_pitch}</span>
                          : (w.partner.tagline || w.partner.customer_benefit || '—')}
                      </p>
                    </div>
                    <div>
                      {isEditing ? (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            disabled={savingPitch}
                            onClick={() => savePitch(w.partner.slug)}
                            className="bg-[#00F0FF] text-black hover:bg-[#00C2CC]"
                            data-testid={`save-pitch-${w.partner.slug}`}
                          >
                            {savingPitch ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEditing}
                            disabled={savingPitch}
                            className="border-white/20"
                          >
                            <XIcon className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEditing(w)}
                          className="border-white/20 text-white"
                          data-testid={`edit-pitch-${w.partner.slug}`}
                        >
                          <Pencil className="w-3 h-3 mr-1.5" />
                          {w.partner.weekly_spotlight_pitch ? 'Edit pitch' : 'Set custom pitch'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Inline pitch editor */}
                  {isEditing && (
                    <div className="mb-4 p-3 rounded-xl bg-black/40 border border-[#00F0FF]/20">
                      <Input
                        value={pitchDraft}
                        onChange={(e) => setPitchDraft(e.target.value)}
                        placeholder="Custom one-liner used in the spotlight banner & social posts (leave blank for auto-template)"
                        maxLength={240}
                        className="bg-black/60 border-white/10"
                        data-testid={`pitch-input-${w.partner.slug}`}
                      />
                      <p className="text-[11px] text-neutral-500 mt-1">
                        {pitchDraft.length}/240 · saves to this partner globally — affects every future week they're up
                      </p>
                    </div>
                  )}

                  {/* Copy-ready posts */}
                  <div className="grid md:grid-cols-2 gap-3">
                    {/* X */}
                    <div className="rounded-xl bg-black/50 border border-white/10 p-3" data-testid={`x-post-block-${rowKey}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-white">X (Twitter)</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copy(w.x_post, `x-${rowKey}`)}
                          className="h-7 px-2 text-[#00F0FF] hover:text-[#00C2CC] hover:bg-white/5"
                          data-testid={`copy-x-${rowKey}`}
                        >
                          {copiedKey === `x-${rowKey}` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          <span className="ml-1 text-xs">{copiedKey === `x-${rowKey}` ? 'Copied' : 'Copy'}</span>
                        </Button>
                      </div>
                      <pre className="whitespace-pre-wrap break-words text-xs text-neutral-300 font-sans leading-relaxed max-h-40 overflow-y-auto">
                        {w.x_post}
                      </pre>
                      <p className="text-[10px] text-neutral-600 mt-2">{w.x_post.length}/280</p>
                    </div>

                    {/* LinkedIn */}
                    <div className="rounded-xl bg-black/50 border border-white/10 p-3" data-testid={`li-post-block-${rowKey}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-white">LinkedIn</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copy(w.linkedin_post, `li-${rowKey}`)}
                          className="h-7 px-2 text-[#00F0FF] hover:text-[#00C2CC] hover:bg-white/5"
                          data-testid={`copy-li-${rowKey}`}
                        >
                          {copiedKey === `li-${rowKey}` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          <span className="ml-1 text-xs">{copiedKey === `li-${rowKey}` ? 'Copied' : 'Copy'}</span>
                        </Button>
                      </div>
                      <pre className="whitespace-pre-wrap break-words text-xs text-neutral-300 font-sans leading-relaxed max-h-40 overflow-y-auto">
                        {w.linkedin_post}
                      </pre>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
